import { ReactElement, useEffect, useRef, useState } from 'react';
import { AddressErrors, IAddress, State, ZipInfo } from 'models/Address';
import { ErrorMessage } from 'config';
import { unmaskPhone } from 'utils/transforms';
import { get } from 'utils/network';
import { Component as CaretDown } from 'icons/icon-ui--caret-outline.svg';
import { Component as CaretUp } from 'icons/icon-ui--caret-up-outline.svg';
import { Checkbox } from '../shared/checkbox/Checkbox';
import { Button } from '../shared/button/Button';
import { useMenuWithCart } from '../../contexts/MenuWithCart';
import { ShippingAddressForm } from '../shared/address-form/ShippingAddressForm';
import { MAX_ADDRESS2_LENGTH } from '../../utils/config';
import { SignupStep } from '../../constants/signup';
import { useCurrentUserQueryOnLoggedOut } from '../../services/user/user.queries';
import { useAddress } from '../../contexts/AddressContext';
import { AddressSignupDraft } from '../../models/address.types';
import { gtmTrackAccountCreated } from '../../services/gtm/gtm';
import { useCreateAddressMutation } from '../../services/address/address.queries';
import { useSaveCartMutation } from '../../services/cart/cart.queries';
import { Cart } from '../../models/cart.types';
import styles from './CreateAddress.module.scss';
import { useQueryClient } from '@tanstack/react-query';

enum ComponentLengthType {
  STREET_NUMBER = 'short_name',
  ROUTE = 'long_name',
}

type CreateAddressProps = {
  isChangeCartAddress?: boolean;
  cartShippingAddressId?: number;
}

export function CreateAddress({
  isChangeCartAddress,
  cartShippingAddressId,
}: CreateAddressProps): ReactElement {
  const {
    menu,
    cart,
    setCurrentSignupStep,
    displayChangeCartAddress,
    setDisplayChangeCartAddress,
    setCartShippingAddressId,
  } = useMenuWithCart();

  const {
    addressSignupDraft,
    setAddressSignupDraft,
    refreshAddresses,
  } = useAddress();

  const {
    firstName,
    lastName,
    address1,
    address2,
    zip,
    state,
    city,
    phone,
    deliveryInstructions,
    smsAccountAlerts,
    smsOffersAlerts,
  } = addressSignupDraft;
  const createAddressMutation = useCreateAddressMutation();
  const saveCartMutation = useSaveCartMutation();

  const [smsAccountExpanded, setSmsAccountExpanded] = useState(false);
  const [smsOffersExpanded, setSmsOffersExpanded] = useState(false);
  const socialSignup = localStorage.getItem('socialSignup');

  // refs used to scroll to error
  const baseRef = useRef<HTMLDivElement | null>(null);
  const firstNameRef = useRef<HTMLDivElement | null>(null);
  const lastNameRef = useRef<HTMLDivElement | null>(null);
  const addressInputRef = useRef<HTMLDivElement | null>(null);
  const address2InputRef = useRef<HTMLDivElement | null>(null);
  const zipRef = useRef<HTMLDivElement | null>(null);
  const stateRef = useRef<HTMLDivElement | null>(null);
  const cityRef = useRef<HTMLDivElement | null>(null);
  const deliveryInstructionsRef = useRef<HTMLDivElement | null>(null);
  const localPhoneNumberRef = useRef<HTMLDivElement | null>(null);
  const refsArray = useRef([
    { base: baseRef },
    { first_name: firstNameRef },
    { last_name: lastNameRef },
    { address: addressInputRef },
    { address2: address2InputRef },
    { zip: zipRef },
    { state: stateRef },
    { city: cityRef },
    { local_phone_number: localPhoneNumberRef },
    { delivery_instructions: deliveryInstructionsRef },
  ]);

  const [isLoading, setLoading] = useState(false);
  const [errors, setErrors] = useState<AddressErrors>({} as AddressErrors);
  const getErrorMsg = (attr) => (errors[attr] ? errors[attr][0] : '');
  const isInvalid = (attr) => !!errors[attr];
  const currentUser = useCurrentUserQueryOnLoggedOut(true).data;

  useEffect(() => {
    (async () => {
      if (zip.length === 5) {
        const response = await get<ZipInfo>(`/zip_codes/${zip}`);

        if (response.ok) {
          const code = response.parsedBody?.state_code;
          const cityName = response.parsedBody?.city;

          if (code && cityName) {
            setAddressSignupDraft((prev) => ({
              ...prev,
              state: code,
              city: cityName,
            }));
          }
        }
      }
    })();
  }, [setAddressSignupDraft, zip]);

  // google autocomplete logic
  useEffect(() => {
    if (window.google && addressInputRef.current) {
      const inputElement = document.getElementById(
        'add-address-address',
      ) as HTMLInputElement | null;

      if (!inputElement) return;
      const autoComplete = new window.google.maps.places.Autocomplete(
        (inputElement),
        {
          types: ['geocode'],
          componentRestrictions: { country: 'us' },
        },
      );

      autoComplete.addListener('place_changed', () => {
        const components = autoComplete.getPlace()?.address_components;
        if (components === undefined) return;

        const newAddress1 = [
          components[0][ComponentLengthType.STREET_NUMBER],
          components[1][ComponentLengthType.ROUTE],
        ].join(' ');
        setAddressSignupDraft((prev) => ({
          ...prev,
          address1: newAddress1,
          state: '',
          city: '',
          zip: '',
        }));

        let nextState = '';
        let nextCity = '';
        let nextZip = '';
        for (let i = 0; i < components.length; i++) {
          const addressType = components[i].types[0];
          const addressLengthType = ComponentLengthType[addressType.toUpperCase()]
            || 'short_name';
          if (addressType !== 'street_number' && addressType !== 'route') {
            const val = components[i][addressLengthType];
            if (addressType === 'administrative_area_level_1') {
              nextState = val;
            } else if (addressType === 'locality') {
              nextCity = val;
            } else if (addressType === 'postal_code') nextZip = val;
          }
        }
        setAddressSignupDraft((prev) => ({
          ...prev,
          state: nextState || prev.state,
          city: nextCity || prev.city,
          zip: nextZip || prev.zip,
        }));
      });
    }
  }, []);

  useEffect(() => {
    if (address2.length <= MAX_ADDRESS2_LENGTH - 1) {
      setErrors((lastErrors) => {
        const newErrors = { ...lastErrors };
        delete newErrors.address2;
        return newErrors;
      });
    } else {
      setErrors((lastErrors) => ({
        ...lastErrors,
        address2: [ErrorMessage.CHAR_LIMIT_ADDRESS2],
      }));
    }
  }, [address2]);

  useEffect(() => {
    if (Object.keys(errors).length === 0) return;

    // Looping over the refsArray to find the first one with an error
    refsArray.current.some((ref) => {
      const fieldName = Object.keys(ref)[0];
      if (errors[fieldName] && ref[fieldName].current) {
        ref[fieldName].current.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
        return true;
      }
      return false;
    });
  }, [errors, refsArray]);

  useEffect(() => {
    if (socialSignup === 'true' && currentUser?.isSocialSignup) {
      gtmTrackAccountCreated(currentUser!);
      localStorage.removeItem('socialSignup');
    }
  }, [socialSignup, currentUser]);

  const queryClient = useQueryClient();
  const handleSubmit = async () => {
    const stateKey = state as keyof typeof State;
    const address: IAddress = {
      address: address1,
      address2,
      city,
      delivery_instructions: deliveryInstructions,
      sms_notification_status: smsAccountAlerts
        ? 'sms_opted_in'
        : 'sms_opted_out',
      sms_offers_status: smsOffersAlerts
        ? 'sms_opted_in'
        : 'sms_opted_out',
      first_name: firstName,
      last_name: lastName,
      local_phone_number: unmaskPhone(phone),
      primary: !isChangeCartAddress, // address is primary if not isChangeCartAddress
      state: stateKey,
      zip,
    };

    setLoading(true);
    const response = await createAddressMutation.mutateAsync(address);
    setLoading(false);

    if (response.status === 422) {
      const parsedBody = response.parsedBody as AddressErrors;

      if (address1 && parsedBody.address && Object.keys(parsedBody).length === 1) {
        parsedBody.base = [ErrorMessage.UNVERIFIED_ADDRESS];
        if (address2) parsedBody.address2 = parsedBody.address;
      } else if (address1) {
        delete parsedBody.address;
      }

      setErrors(parsedBody);
    } else {
      setErrors({} as AddressErrors);
      if (isChangeCartAddress && displayChangeCartAddress) {
        const parsedBody = response.parsedBody as IAddress;

        if (cartShippingAddressId && cart?.id && parsedBody.id) {
          // change address of this cart to the new address
          const updatedCart:Cart = { ...cart, shippingAddressId: parsedBody.id };
          await saveCartMutation.mutateAsync(updatedCart);

          // refresh and set id so WeeklyAddressChange component is updated
          await refreshAddresses();
          setCartShippingAddressId(parsedBody.id);
        }
        setDisplayChangeCartAddress(false);
      } else {
        queryClient.invalidateQueries({ queryKey: ['loggedInMenu', menu?.id] });
        setCurrentSignupStep(SignupStep.PAYMENT);
      }
    }
  };

  const handleFieldsChange = (update: AddressSignupDraft) => {
    setAddressSignupDraft((prev: AddressSignupDraft) => ({
      ...prev,
      ...update,
    }));
  };

  return (
    <div className={styles.address_signup_container}>
      <div className={styles.scroll_area}>
        <ShippingAddressForm
          values={{
            firstName,
            lastName,
            address1,
            address2,
            zip,
            state,
            city,
            phone,
            deliveryInstructions,
          }}
          onChange={handleFieldsChange}
          getErrorMsg={getErrorMsg}
          isInvalid={isInvalid}
          refs={{
            baseRef,
            firstNameRef,
            lastNameRef,
            addressInputRef,
            address2InputRef,
            zipRef,
            cityRef,
            stateRef,
            deliveryInstructionsRef,
            localPhoneNumberRef,
          }}
          className={styles.address_form}
        />
        <div className={styles.address_checkbox_section}>
          <div className={styles.address_checkbox_container}>
            <div className={styles.address_checkbox}>
              <Checkbox
                id="Order & Account Notifications"
                isChecked={smsAccountAlerts}
                onChange={(e) => setAddressSignupDraft((prev) => ({
                  ...prev,
                  smsAccountAlerts: e.target.checked,
                }))}
              />
              <div
                role="button"
                tabIndex={0}
                onClick={() => setSmsAccountExpanded(!smsAccountExpanded)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSmsAccountExpanded(!smsAccountExpanded);
                  }
                }}
                className={styles.address_checkbox_expand_button}
              >
                Order & Account Notifications
                {smsAccountExpanded ? <CaretUp width="15" />
                  : <CaretDown width="16px" height="8px" />}
              </div>
            </div>
            {smsAccountExpanded && (
              <p>
                Get order updates, tracking details, and other important account alerts.
              </p>
            )}
          </div>
          <div className={styles.address_checkbox_container}>
            <div className={styles.address_checkbox}>
              <Checkbox
                id="Offers & Updates"
                isChecked={smsOffersAlerts}
                onChange={(e) => setAddressSignupDraft((prev) => ({
                  ...prev,
                  smsOffersAlerts: e.target.checked,
                }))}
              />
              <div
                role="button"
                tabIndex={0}
                onClick={() => setSmsOffersExpanded(!smsOffersExpanded)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSmsOffersExpanded(!smsOffersExpanded);
                  }
                }}
                className={styles.address_checkbox_expand_button}
              >
                Offers & Updates
                {smsOffersExpanded ? <CaretUp width="15" />
                  : <CaretDown width="16px" height="8px" />}
              </div>
            </div>
            {smsOffersExpanded && (
              <p>
                Receive exclusive deals, personalized menu recommendations, and more!
                <br />
                <br />
                By submitting this form, you agree to receive recurring, automated promotional and
                personalized marketing text messages and/or important account updates from Purple
                Carrot at the phone number used when signing up. Message frequency will
                vary—don&apos;t worry we promise not to spam you! Message and data rates will
                apply.
                Reply HELP for help or STOP to cancel. Consent is not a condition of purchase.
                <br />
                View
                {' '}
                <a
                  target="_blank"
                  rel="noreferrer"
                  href="/terms"
                >
                  Terms & Conditions
                </a>
                &nbsp;and&nbsp;
                <a
                  target="_blank"
                  rel="noreferrer"
                  href="/privacy"
                >
                  Privacy Policy
                </a>
                .
              </p>
            )}
          </div>
          <span>* Required Fields</span>
        </div>
      </div>
      {isChangeCartAddress ? (
        <div className={styles.change_address_button_container}>
          <Button
            className={[styles.change_address_button, styles.change_address_cancel_button].join(' ')}
            text="Cancel"
            onClick={() => setDisplayChangeCartAddress(false)}
          />
          <Button
            className={styles.change_address_button}
            text="Save"
            onClick={handleSubmit}
            isEnabled={!isLoading && !cart?.priceSummary?.remainingMinimumAmount}
          />
        </div>
      ) : (
        <Button
          className={[styles.full_width, styles.next_button].join(' ')}
          text="Next"
          onClick={handleSubmit}
          isEnabled={!isLoading && !cart?.priceSummary?.remainingMinimumAmount}
        />
      )}
    </div>
  );
}
